// ONE composer for every lead text (Founder 2026-09-28: "אשמח לדבר עם ליאור לגבי אינסטלטור" is not proper Hebrew —
// the spec is "אשמח לדבר עם ליאור (אינסטלטור)", and when the inquiry has details, "לגבי <details>" in proper Hebrew).
// Plain JS so the site (TS), the server and scripts/wa-worker.mjs share it. The ROOT rule: the profession NEVER
// follows "לגבי" — it is the WHO (in parentheses after the name, or the noun of the expert); "לגבי" takes only the
// DETAIL phrase: the questionnaire need + "ב<city>". hebrewIssues() validates the result; callers fall back to the
// short correct form when it fails, and every fix passes the duplicate guard (lib/msgGuard.mjs).
import { guardMessage } from './msgGuard.mjs';
import { CATEGORY_FIELD } from './categoryField.mjs';
// LAW 04/10/26 (Hebrew proof of every message): the visitor + pro texts are BUILT by lib/heMsg.mjs (one builder + proof gate).
import { leadText, proText } from './heMsg.mjs';

const clean = s => String(s ?? '').replace(/\s+/g, ' ').replace(/^[\s,.;:–—-]+|[\s,.;:–—-]+$/g, '').trim();
const norm = s => clean(s).replace(/["'״׳()]/g, '').toLowerCase();

// Words that can NOT open the object of "לגבי" (verbs, quantifiers, numbers, question words, answers) — found by running the
// composer over every real questionnaire answer (2026-09-28): "עד 80", "12-20 מ״ר", "יש כבר הליך פתוח", "עדיין לא נקבע דיון",
// "כמה מזגנים עיליים", "רוצה גם לבדוק…", "מחפש/ת…". Those details go on their own line: "פרטי הפנייה: …".
const NOT_NOUN_START = new Set(['עד', 'מעל', 'פחות', 'בין', 'יותר', 'כ־', 'כ-', 'יש', 'אין', 'כמה', 'עדיין', 'כבר', 'גם', 'רק', 'רוצה', 'רוצים', 'מעוניין',
  'מעוניינת', 'מחפש', 'מחפשת', 'מחפשים', 'מחפש/ת', 'צריך', 'צריכה', 'צריכים', 'צריך/ה', 'אני', 'אנחנו', 'תודה', 'כן', 'לא', 'מה', 'איך', 'למה', 'מתי', 'האם',
  'שלום', 'היי', 'לפני', 'אחרי', 'הייתי', 'היה', 'קיבלתי', 'נפלתי', 'עבר', 'עברה', 'לא יודע', 'לא יודעת']);
// Answers that carry no information once the question is gone.
const EMPTY_ANSWER = new Set(['כן', 'לא', 'תודה', 'תודה רבה', 'אין', 'לא יודע', 'לא יודעת', 'לא בטוח', 'לא בטוחה', 'אחר', 'דלג', 'ללא']);
// A topic-title remainder that is only a modifier ("מומלץ", "זול ליד", "דחוף") — never the object of "לגבי".
const MOD_ONLY = /^(?:ה?(?:מומלץ|מומלצת|מומלצים|זול|זולה|זולים|טוב|טובה|הכי טוב|מקצועי|מקצועית|אמין|מנוסה|מוסמך|מוסמכת|פרטי|דחוף|קרוב|באזור|ליד|לידי|בזול)(?:\s+|$))+$/;
export function startsAsNoun(detail) {
  const w = clean(detail).split(/[\s,]+/)[0] || '';
  if (!w) return false;
  if (/^[\d₪%]/.test(w)) return false;
  return !NOT_NOUN_START.has(w) && !NOT_NOUN_START.has(w.replace(/[/].*$/, ''));
}

/** The phrase that follows "לגבי": "בעיה באסלה/ניאגרה באשדוד" | "עבודה באשדוד" | "". Never the category itself. */
export function leadDetail({ need, city, categoryName } = {}) {
  const cat = norm(categoryName), c = clean(city);
  // split on clause commas only — a comma between digits is a thousands separator ("עד 2,000 ₪" came out "2, 000")
  let parts = clean(need).split(/(?<!\d),\s*|,(?!\d)\s*/).map(clean).filter(Boolean)
    .filter(p => !EMPTY_ANSWER.has(p))                                                   // "תודה רבה", a bare "כן"/"לא"
    .filter(p => norm(p) !== cat && norm(p) !== norm(c) && norm(p) !== norm('ב' + c));        // no profession, no bare city
  // bughunter 01/10/26 (kwlab topic titles lead the need): "עורך דין מקרקעין מומלץ" → "לגבי מומלץ"; "עורך דין רשלנות רפואית" under
  // עורך דין נזיקין → "עורך דין… לגבי עורך דין רשלנות רפואית". The first clause loses the category (or its ≥2-word profession head),
  // and a clause left with only a modifier (מומלץ / זול / ליד / דחוף…) is no subject at all → dropped.
  if (parts.length && cat) {
    const cw = clean(categoryName).split(' '); const w = parts[0].split(' '); let p0 = parts[0];
    if (norm(p0).startsWith(cat + ' ')) p0 = clean(p0.slice(clean(categoryName).length));
    else if (cw.length >= 2) { let k = 0; while (k < cw.length - 1 && k < w.length && norm(w[k]) === norm(cw[k])) k++; if (k >= 2 && w.length > k) p0 = w.slice(w.length - k >= 2 ? k : k - 1).join(' '); } // 04/10: "סגירת מרפסת גדולה" → "מרפסת גדולה", never "גדולה"
    if (MOD_ONLY.test(p0)) parts.shift(); else parts[0] = p0;
  }
  let n = parts.join(', ');
  if (n && cat && norm(n).startsWith(cat + ' ')) n = clean(n.slice(clean(categoryName).length)); // "אינסטלטור בעיה…" → "בעיה…"
  const cityInNeed = c && n.includes(c);
  if (n && c && !cityInNeed) return `${n} ב${c}`;
  if (n) return n;
  if (c) return `עבודה ב${c}`;
  return '';
}

/** Visitor → OUR WhatsApp. With a pro: "אשמח לדבר עם ליאור (אינסטלטור)"; without: "אשמח לדבר עם <expertNoun>". */
export function visitorLeadMessage({ proName, proGender, categoryName, expertNoun, need, city, ticket, topic, hideClickCode, greeting } = {}) {
  // 04/10/26: "אשמח לדבר עם מומחה לסגירת מרפסת זכוכית (מרפסת קטנה, רמת השרון)" — lib/heMsg.mjs leadText (proofed; a business name
  // is never the addressee). `expertNoun` is derived there from the category (same SERVICE_WORDS rule as lib/categoryPhrase.ts).
  // `hideClickCode` (Founder 12/10/26, פנייה 14065): the CALL door passes this through so the customer never sees "קוד: N".
  const cat = clean(categoryName);
  return leadText({ categoryName: cat, field: (cat && CATEGORY_FIELD[cat]) || null, proName, proGender, need, city, ticket, topic, hideClickCode, greeting });
}

/** "דיני תעבורה" for "עורך דין תעבורה" — the FIELD of a category (lib/categoryField.mjs), or null. */
export function categoryField(categoryName) { const n = clean(categoryName); return (n && CATEGORY_FIELD[n]) || null; }

/** OUR tracked link → the pro. v4 (Founder 2026-09-29, ticket 13594 + Hebrew gate v2):
 *  "שלום חיים, מצאתי אותך באתר המומחים ואשמח לדבר לגבי התייעצות בנושא דיני תעבורה בנתניה – דוח מהירות, פעם ראשונה."
 *  - the SUBJECT always (lib/categoryField.mjs — an action word fitting the category), never the professional's noun;
 *  - gender-neutral both ways: "אותך" is one spelling for m/f unvocalized ("אליך" reads masculine — the gate caught it);
 *  - the city belongs to the subject, a noun-phrase answer follows a dash, a non-noun answer gets "פרטי הפנייה:".
 *  `detail` = a ready detail phrase (legacy callers); otherwise built from need + city. Any gate issue → the short form. */
export function composeProLead({ proName, detail, need, city, categoryName } = {}) {
  const name = clean(proName) || 'שם';
  const field = categoryField(categoryName);
  const intro = `שלום ${name}, מצאתי אותך באתר המומחים ואשמח לדבר`;
  const subject = field ? ` לגבי ${field}` : '';
  const opts = { categoryNames: [categoryName], neutral: true };
  // sim 09-29 on real answers: dashes inside an answer ("…25% — עסקה") read as raw form output → commas; a size/amount ("12-20 מ״ר")
  // reads fine after the dash — only a clause-like answer ("עדיין לא נקבע") gets the label
  const tailOf = (lead, raw) => { const answer = clean(String(raw || '').replace(/\s+[—–-]\s+/g, ', '));
    return !answer ? `${lead}.` : startsAsNoun(answer) || /^[\d₪]/.test(answer) ? `${lead} – ${answer}.` : `${lead}. פרטי הפנייה: ${answer}.`; };
  // 04/10/26: the need/city path is lib/heMsg.mjs proText — "שלום שי, מצאתי אותך … לגבי <תחום> (<תשובה>, <עיר>)."
  if (detail === undefined) return proText({ proName, categoryName, field, need, city });
  let text;
  if (detail === undefined) {
    const c = clean(city);
    text = tailOf(`${intro}${subject || (c ? ' לגבי עבודה' : '')}${c ? ` ב${c}` : ''}`, leadDetail({ need, categoryName }));
  } else {
    let d = clean(detail); if (d === 'עבודה') d = '';                          // templateDetail's filler carries nothing
    text = field || !d || !startsAsNoun(d) ? tailOf(`${intro}${subject}`, d) : `${intro} לגבי ${d}.`;
  }
  if (hebrewIssues(text, opts).length) text = `${intro}${subject}.`;
  return guardMessage(text).text;
}
export function proLeadMessage({ proName, need, city, categoryName } = {}) {
  return composeProLead({ proName, need, city, categoryName });
}

/** For fixed template wording "…לגבי {{n}}" (Meta-approved, can't change): ALWAYS a noun phrase —
 *  "בעיה באסלה באשדוד" | "עבודה בחיפה (עדיין לא נקבע דיון)" | "עבודה". */
export function templateDetail({ need, city, categoryName } = {}) {
  const d = leadDetail({ need, city, categoryName });
  if (d && startsAsNoun(d)) return d;
  const c = clean(city); const raw = leadDetail({ need, categoryName });
  return `עבודה${c ? ` ב${c}` : ''}${raw && !startsAsNoun(raw) ? ` (${raw})` : ''}`;
}

/** Hebrew-validity checks for a composed lead text. [] = clean. */
// Gendered words a message to/from an unknown person must not carry (Founder 2026-09-29: "ניסוח שיתאים לזכר ולנקבה").
// "מחפש/ת" (slash form) is neutral and passes. The professional's own noun (a WHO category — "עורך דין", "אינסטלטור") genders
// the pro, so in a neutral text it is an issue too; its field (lib/categoryField.mjs) is the neutral way to name the subject.
const GENDERED = ['מחפש', 'מחפשת', 'מעוניין', 'מעוניינת', 'צריך', 'צריכה', 'פניתי אליך כי אתה', 'אתה', 'שלך אדוני', 'גברתי', 'אדוני', 'יקר', 'יקרה', 'זקוק', 'זקוקה'];
export function genderIssues(text, { categoryNames = [] } = {}) {
  const t = String(text ?? ''); const out = [];
  for (const w of GENDERED) if (new RegExp(`(^|[^א-ת])${w}(?![א-ת/])`).test(t)) out.push(`מילה ממוגדרת: "${w}" (ניסוח ניטרלי לזכר ולנקבה)`);
  for (const raw of categoryNames.map(clean).filter(Boolean)) {
    const f = CATEGORY_FIELD[raw];
    if (f && f !== raw && !f.includes(raw) && new RegExp(`(^|[^א-ת])${raw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![א-ת])`).test(t)) out.push(`שם בעל המקצוע "${raw}" ממגדר את הנמען — לכתוב "לגבי ${f}"`);
  }
  return out;
}

export function hebrewIssues(text, { categoryNames = [], neutral = false } = {}) {
  const t = String(text ?? ''); const out = [];
  if (neutral) out.push(...genderIssues(t, { categoryNames }));
  for (const raw of categoryNames.map(clean).filter(Boolean)) {
    const esc = raw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const isService = CATEGORY_FIELD[raw] === raw; // "ביטוח דירה", "דלתות פנים" — the service itself is a fine object of "לגבי"
    if (!isService && new RegExp(`לגבי\\s+${esc}(?![א-ת])`).test(t)) out.push(`"לגבי" לפני שם המקצוע (${raw})`);
    if (new RegExp(`שמתאימים\\s+ל${esc}(?![א-ת])`).test(t)) out.push(`"שמתאימים ל" + שם מקצוע (${raw})`);
  }
  const after = t.match(/לגבי\s+([^.\n]+)/); if (after && !startsAsNoun(after[1])) out.push(`"לגבי" לפני ביטוי שאינו שם עצם ("${after[1].slice(0, 25)}")`);
  if (/\(\s*\)/.test(t)) out.push('סוגריים ריקים');
  if (/לגבי\s*([.,!?]|$)/m.test(t)) out.push('"לגבי" בלי המשך');
  if (/\s[,.]/.test(t.replace(/\n/g, ' '))) out.push('רווח לפני סימן פיסוק');
  if (/,\s*,|\.\s*\./.test(t)) out.push('פיסוק כפול');
  if (/\b(undefined|null|NaN)\b|\[object/.test(t)) out.push('ערך ריק בטקסט');
  if (/ב\s+[א-ת]/.test(t) && / ב\s+[א-ת]/.test(t)) out.push('"ב" מנותקת');
  out.push(...guardMessage(t).fixed);
  return out;
}
